/**
 * WordPress dependencies
 */
import { useEffect, useState } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';

/**
 * Internal dependencies
 */
import type { UseJobsReturn, JobPosting, JobBoardProvider } from '../types';

interface JobsApiResponse {
	provider: string;
	jobs: JobPosting[];
	cached: boolean;
	job_count: number;
	synced_at?: number;
	expires_at?: number;
}

/**
 * Hook to fetch jobs from WordPress REST API
 * All provider logic and caching is handled server-side
 */
export function useJobs( provider: JobBoardProvider, settings: Record< string, unknown > = {} ): UseJobsReturn {
	const [
		jobs,
		setJobs,
	] = useState< JobPosting[] >( [] );
	const [
		error,
		setError,
	] = useState< Error | null >( null );
	const [
		loading,
		setLoading,
	] = useState< boolean >( true );

	useEffect( () => {
		let cancelled = false;

		const fetchJobs = async () => {
			try {
				setLoading( true );
				setError( null );

				// Build URL with provider and settings
				const params = new URLSearchParams();
				params.set( 'provider', provider );
				params.set( 'settings', JSON.stringify( settings ) );

				const data = await apiFetch< JobsApiResponse >( {
					path: `/wdg/v1/jobs?${ params.toString() }`,
				} );

				if ( ! cancelled ) {
					setJobs( data.jobs );
				}
			} catch ( err ) {
				if ( ! cancelled ) {
					setError( err instanceof Error ? err : new Error( String( err ) ) );
				}
			} finally {
				if ( ! cancelled ) {
					setLoading( false );
				}
			}
		};

		void fetchJobs();

		return () => {
			cancelled = true;
		};
	}, [ provider, settings ] );

	return { jobs, error, loading };
}
